import { useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import type { TimeEntry, Expense } from '../types/billing.types';

const API_BASE = '';

interface UseUnbilledItemsReturn {
  timeEntries: TimeEntry[];
  expenses: Expense[];
  loading: boolean;
  error: string | null;
  fetchForMatter: (matterId: string, periodStart?: string, periodEnd?: string) => Promise<void>;
}

export const useUnbilledItems = (): UseUnbilledItemsReturn => {
  const { keycloak } = useKeycloak();
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchForMatter = useCallback(
    async (matterId: string, periodStart?: string, periodEnd?: string) => {
      setLoading(true);
      setError(null);
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${keycloak?.token || ''}`,
        };

        const teParams = new URLSearchParams({
          matter_id: matterId,
          billing_status: 'unbilled',
          limit: '500',
          order_by: 'entry_date',
          order_direction: 'ASC',
        });
        if (periodStart) teParams.set('date_from', periodStart);
        if (periodEnd) teParams.set('date_to', periodEnd);

        // Also fetch ready_to_bill items
        const rtbParams = new URLSearchParams(teParams);
        rtbParams.set('billing_status', 'ready_to_bill');

        const expParams = new URLSearchParams({
          matter_id: matterId,
          billing_status: 'unbilled',
          limit: '500',
          order_by: 'expense_date',
          order_direction: 'ASC',
        });
        if (periodStart) expParams.set('date_from', periodStart);
        if (periodEnd) expParams.set('date_to', periodEnd);

        const expRtbParams = new URLSearchParams(expParams);
        expRtbParams.set('billing_status', 'ready_to_bill');

        const [teRes, teRtbRes, expRes, expRtbRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/billing/time-entries?${teParams}`, { headers }),
          fetch(`${API_BASE}/api/v1/billing/time-entries?${rtbParams}`, { headers }),
          fetch(`${API_BASE}/api/v1/billing/expenses?${expParams}`, { headers }),
          fetch(`${API_BASE}/api/v1/billing/expenses?${expRtbParams}`, { headers }),
        ]);

        if (!teRes.ok || !teRtbRes.ok || !expRes.ok || !expRtbRes.ok) {
          throw new Error('Failed to fetch unbilled items');
        }

        const [teJson, teRtbJson, expJson, expRtbJson] = await Promise.all([
          teRes.json(),
          teRtbRes.json(),
          expRes.json(),
          expRtbRes.json(),
        ]);

        setTimeEntries([...(teJson.data || []), ...(teRtbJson.data || [])]);
        setExpenses([...(expJson.data || []), ...(expRtbJson.data || [])]);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch unbilled items');
        setTimeEntries([]);
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    },
    [keycloak?.token]
  );

  return { timeEntries, expenses, loading, error, fetchForMatter };
};
